import React, { useEffect, useMemo, useRef } from 'react';

import { Box } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import { getColumnDefinitionByType } from 'screens/performanceReview/manageAndInsights/TalentPlanning/columnDefinitions';
import { checkStaticDataCompensationEnabled } from 'utils/helpers/featureFlags';

import { Delete } from 'assets/assetsV1/iconComponents/Delete';

import { isMobileView } from 'constants/isMobileView';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';

import { StakeholderMapColumn, StakeholderMapRow, useGetStakeholderMap } from '../dataHooks';
import { mapEmployeeForMultiStepMenu } from './StakeHolderTreeTable';
import { StakeholderErrorState, StakeholderEmptyState } from './shared/stakeholderComponents';
import {
  MOCK_COLUMNS_STAKEHOLDER_FLAT_LIST,
  MOCK_ROWS_STAKEHOLDER_FLAT_LIST,
  STAKEHOLDER_OPTIONS_FLAT_LIST,
} from './shared/stakeholderConstants';
import { StakeHolderTableProps } from './shared/stakeholderTypes';
import { createMultiSelectMenuRendererFlatList } from './shared/stakeholderUtils';
import { useStakeholderTable } from './shared/useStakeholderTable';
import {
  setModifiedStakeholdersRows,
  setOriginalStakeholdersRows,
  useCompensationDrawersState,
} from './slice/compensationDrawersSlice';

const EMPTY_COLUMNS: StakeholderMapColumn[] = [];
const EMPTY_ROWS: StakeholderMapRow[] = [];

export const additionalColumnsPropsMapStakeholderFlatList = {
  EMPLOYEE: {
    columnId: 'employee',
    displayName: 'Employees',
    columnType: 'EMPLOYEE',
    flex: 1,
    autoHeight: true,
    metadata: { isEmployeeSubtitle: true },
    headerName: 'Employee',
    lockPosition: 'left',
  },
  PROPOSER: {
    headerName: 'Proposer',
    cellRenderer: createMultiSelectMenuRendererFlatList(),
    cellRendererParams: {
      options: STAKEHOLDER_OPTIONS_FLAT_LIST,
    },
    minWidth: 240,
  },
  APPROVER: {
    headerName: 'Approver',
    cellRenderer: createMultiSelectMenuRendererFlatList(),
    cellRendererParams: {
      options: STAKEHOLDER_OPTIONS_FLAT_LIST,
    },
    minWidth: 240,
  },
  PUBLISHER: {
    headerName: 'Publisher',
    cellRenderer: createMultiSelectMenuRendererFlatList(),
    cellRendererParams: {
      options: STAKEHOLDER_OPTIONS_FLAT_LIST,
    },
    minWidth: 240,
  },
};

function StakeHolderFlatListTable({ gridRef, gridStatePersistenceKey }: StakeHolderTableProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentCompRef = useRef<any>(null);
  const agGridRef = gridRef ?? currentCompRef;
  const { modifiedStakeholdersRows } = useCompensationDrawersState();
  const dispatch = useDispatch();
  const {
    data: stakeholderMapData,
    isLoading: isStakeholderMapLoading,
    error,
  } = useGetStakeholderMap({
    compensationId: 1, // TODO : make it dynamic
  });

  const columns = useMemo(() => stakeholderMapData?.columns ?? EMPTY_COLUMNS, [stakeholderMapData?.columns]);
  const rows = useMemo(() => stakeholderMapData?.rows ?? EMPTY_ROWS, [stakeholderMapData?.rows]);

  const { columnDefs: staticColumnDefs } = useStakeholderTable({
    columns: MOCK_COLUMNS_STAKEHOLDER_FLAT_LIST,
    rows: MOCK_ROWS_STAKEHOLDER_FLAT_LIST,
  });

  useEffect(() => {
    if (!checkStaticDataCompensationEnabled()) {
      const formattedRows = rows.map((row: StakeholderMapRow) => ({
        ...row,
        PROPOSER: STAKEHOLDER_OPTIONS_FLAT_LIST.ANY_EMPLOYEE.value,
        APPROVER: STAKEHOLDER_OPTIONS_FLAT_LIST.ANY_EMPLOYEE.value,
        PUBLISHER: STAKEHOLDER_OPTIONS_FLAT_LIST.ANY_EMPLOYEE.value,
        proposerSelectedEmployees: row?.PROPOSER?.length > 0 ? row?.PROPOSER?.map(mapEmployeeForMultiStepMenu) : null,
        approverSelectedEmployees: row?.APPROVER?.length > 0 ? row?.APPROVER?.map(mapEmployeeForMultiStepMenu) : null,
        publisherSelectedEmployees:
          row?.PUBLISHER?.length > 0 ? row?.PUBLISHER?.map(mapEmployeeForMultiStepMenu) : null,
        id: row?.EMPLOYEE?.id,
      }));

      dispatch(setOriginalStakeholdersRows(formattedRows));
      dispatch(setModifiedStakeholdersRows(formattedRows));
    }
  }, [dispatch, rows]);

  const columnDefs = useMemo(
    () =>
      checkStaticDataCompensationEnabled()
        ? staticColumnDefs
        : columns.map((col: StakeholderMapColumn) => ({
            ...col,
            colId: col.columnId,
            field: col.columnId,
            id: col.columnId,
            displayName: col.displayName,
            headerName: col.displayName,
            minWidth: 220,
            ...getColumnDefinitionByType(col),
            ...(additionalColumnsPropsMapStakeholderFlatList[col.columnId] || {}),
          })),
    [columns, staticColumnDefs]
  );

  if (modifiedStakeholdersRows?.length === 0 && !isStakeholderMapLoading) {
    return (
      <Box display="flex" alignItems="flex-start" borderRadius="8px" width="100%">
        <Box
          className="stakeholder-flat-list-table"
          width="100%"
          height="calc(100vh - 470px)"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <StakeholderEmptyState heading="No stake holders are available!" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box width="100%" height="calc(100vh - 470px)" display="flex" justifyContent="center" alignItems="center">
        <StakeholderErrorState
          heading="No stake holders are available!"
          description="There was an issue fetching the stake holders. Please try again later."
        />
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-start" borderRadius="8px" width="100%">
      <Box className="stakeholder-flat-list-table" width="100%" height="calc(100vh - 470px)" minHeight="600px">
        <AgGridTable
          key={gridStatePersistenceKey}
          gridStatePersistenceKey={gridStatePersistenceKey}
          ref={agGridRef}
          rowData={modifiedStakeholdersRows}
          getRowId={(params) => params.data.id}
          columnDefs={columnDefs}
          paginationAutoPageSize={!!isMobileView()}
          headerHeight={isMobileView() ? 20 : 40}
          rowHeight={isMobileView() ? 167 : 66}
          sideBar={{
            position: 'right',
            toolPanels: ['columns'],
            defaultToolPanel: undefined,
          }}
          loading={checkStaticDataCompensationEnabled() ? false : isStakeholderMapLoading}
          domLayout="normal"
          pagination
          paginationPageSize={10}
          rowStyle={{ cursor: 'default' }}
          height="100%"
          showSidebarToggle={false}
          defaultColDef={{
            resizable: false,
            cellStyle: { display: 'flex', alignItems: 'center' },
            headerComponent: CustomHeaderCell,
          }}
          kebabMenuIsRowSpecific
          kebabMenuOptionsGenerator={() => []}
          // eslint-disable-next-line react/no-unstable-nested-components
          getUIOptionsForLastColumn={() => () => <Delete boxSize="24px" color="#666666" />}
          uIOptionMaxWidth={60}
        />
      </Box>
    </Box>
  );
}

export { StakeHolderFlatListTable };
